import React, { useState, useEffect, useRef } from 'react';
import apiClient from './services/api';
import RoomCalendar from './RoomCalendar';
import MultiRoomCalendar from './MultiRoomCalendar';
import BookingPanel from './BookingPanel';
import LazyImage from './LazyImage';
import './OurRooms.css';

interface Room {
  id: string;
  room_number: string;
  room_type_name: string;
  description: string;
  floor: number;
  view_type: string;
  max_occupancy: number;
  max_adults: number;
  max_children: number;
  base_price: string;
  amenities: string[];
  images: Array<{ url: string; caption: string; sort_order: number }>;
  custom_features: string[];
  sort_order: number;
}

// Helper: Resim URL'ini düzelt
const getImageUrl = (url: string): string => {
  if (url.startsWith('http')) return url; // External URL
  return url; // /uploads/rooms/xxx.jpg -> nginx proxy ile backend'e gider
};

interface OurRoomsProps {
  onNavigateToReservation?: () => void;
}

const OurRooms: React.FC<OurRoomsProps> = ({ onNavigateToReservation }) => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState<Record<string, number>>({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [calendarView, setCalendarView] = useState<'summary' | 'multi'>('summary'); // Default: genel durum

  // Booking Panel state
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingCheckIn, setBookingCheckIn] = useState('');
  const [bookingCheckOut, setBookingCheckOut] = useState('');
  const [bookingSource, setBookingSource] = useState('ourrooms');
  const [bookingRoomId, setBookingRoomId] = useState<string | undefined>(undefined);
  const [bookingAdults, setBookingAdults] = useState<number | undefined>(undefined);
  const [bookingChildren, setBookingChildren] = useState<number | undefined>(undefined);
  const [bookingChildAges, setBookingChildAges] = useState<number[] | undefined>(undefined);
  const bookingPanelRef = useRef<HTMLDivElement>(null);

  const handleReservationSelect = (data: { checkIn: string; checkOut: string; roomId?: string; adults?: number; children?: number; childAges?: number[]; source?: string }) => {
    setBookingCheckIn(data.checkIn);
    setBookingCheckOut(data.checkOut);
    setBookingSource(data.source || 'ourrooms');
    setBookingRoomId(data.roomId);
    setBookingAdults(data.adults);
    setBookingChildren(data.children);
    setBookingChildAges(data.childAges);
    setBookingOpen(true);
    // Panel'e scroll et
    setTimeout(() => {
      bookingPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      // Database'den admin tarafından düzenlenmiş odaları çek
      const response = await apiClient.get('/rooms');
      
      if (response.data.success && response.data.data) {
        const roomsData = response.data.data;
        setRooms(roomsData);
        
        // Her oda için başlangıç index'i 0 olarak ayarla
        const initialIndexes: Record<string, number> = {};
        roomsData.forEach((room: Room) => {
          initialIndexes[room.id] = 0;
        });
        setCurrentImageIndex(initialIndexes);
      } else {
        setRooms([]);
      }
    } catch (error) {
      console.error('Odalar yüklenemedi:', error);
      setRooms([]);
    } finally {
      setLoading(false);
    }
  };

  const nextImage = (roomId: string, totalImages: number) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [roomId]: ((prev[roomId] || 0) + 1) % totalImages
    }));
  };

  const prevImage = (roomId: string, totalImages: number) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [roomId]: ((prev[roomId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const openLightbox = (roomId: string) => {
    setActiveRoomId(roomId);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Scroll'u engelle
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setActiveRoomId(null);
    document.body.style.overflow = 'auto'; // Scroll'u geri aç
  };

  if (loading) {
    return (
      <div className="our-rooms-page">
        <div className="loading-spinner">Odalar yükleniyor...</div>
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <div className="our-rooms-page">
        <div className="rooms-hero">
          <h1>Odalarımız</h1>
          <p>Henüz oda bilgisi eklenmemiş</p>
        </div>
      </div>
    );
  }

  return (
    <div className="our-rooms-page">
      <div className="rooms-hero">
        <h1>Odalarımız</h1>
        <p>Villa Polikne'de sadeliğin ve konforu bir arada bulacağınız odalarımız</p>
      </div>

      <div className="container">
        {/* Müsaitlik Takvimi - Geçici olarak gizlendi */}
        {/* <section className="availability-calendar-section">
          <div className="calendar-header-section">
            <div>
              <h2>Müsaitlik Takvimi</h2>
              <p className="calendar-description">
                {calendarView === 'multi' 
                  ? 'Odalarımızın müsaitlik durumunu aşağıdaki takvimden inceleyebilir, müsait günlere tıklayarak rezervasyon yapabilirsiniz.'
                  : 'Odalarımızın genel müsaitlik durumunu aşağıdaki takvimden inceleyebilirsiniz.'
                }
              </p>
            </div>
            <div className="calendar-view-toggle">
              <button 
                className={calendarView === 'summary' ? 'active' : ''}
                onClick={() => setCalendarView('summary')}
              >
                Genel Görünüm
              </button>
              <button 
                className={calendarView === 'multi' ? 'active' : ''}
                onClick={() => setCalendarView('multi')}
              >
                Oda Bazlı Görünüm
              </button>
            </div>
          </div>

          {calendarView === 'summary' ? (
            <RoomCalendar 
              onReservationSelect={(data) => {
                handleReservationSelect({
                  checkIn: data.checkInDate,
                  checkOut: data.checkOutDate,
                  roomId: data.roomId || undefined,
                  source: 'ourrooms-summary'
                });
              }}
            />
          ) : (
            <MultiRoomCalendar 
              onReservationSelect={(data) => {
                handleReservationSelect({
                  checkIn: data.checkInDate,
                  checkOut: data.checkOutDate,
                  roomId: data.roomId,
                  adults: data.adults,
                  children: data.children,
                  childAges: data.childAges,
                  source: 'ourrooms-room'
                });
              }}
            />
          )}

          {/* Booking Panel - takvim altında */}
          {/* <div ref={bookingPanelRef}>
            <BookingPanel
              isOpen={bookingOpen}
              checkInDate={bookingCheckIn}
              checkOutDate={bookingCheckOut}
              bookingSource={bookingSource}
              preSelectedRoomId={bookingRoomId}
              initialAdults={bookingAdults}
              initialChildren={bookingChildren}
              initialChildAges={bookingChildAges}
              onClose={() => setBookingOpen(false)}
            />
          </div>
        </section> */}

        <div className="rooms-intro">
          <p>
            Her biri özenle tasarlanmış odalarımız, modern konfor ve geleneksel misafirperverliği bir araya getiriyor. 
            Farklı seçeneklerimizle unutulmaz bir konaklama deneyimi sunuyoruz.
          </p>
        </div>

        <div className="room-types-grid">
          {rooms.map((room) => {
            const currentIndex = currentImageIndex[room.id] || 0;
            const currentImage = room.images && room.images.length > 0 ? room.images[currentIndex] : null;
            
            return (
              <div key={room.id} className="room-type-card">
                {currentImage && (
                  <div className="room-image-carousel">
                    <div
                      className="room-type-image"
                      onClick={() => openLightbox(room.id)}
                      title="Resmi büyütmek için tıklayın"
                    >
                      <LazyImage
                        key={getImageUrl(currentImage.url)}
                        src={getImageUrl(currentImage.url)}
                        alt={room.room_type_name}
                        wrapStyle={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                      />
                      <div className="zoom-hint">🔍</div>
                      <div className="room-capacity">
                        <span>👥 {room.max_occupancy} Kişi</span>
                      </div>
                    </div>
                    
                    {room.images.length > 1 && (
                      <>
                        <button 
                          className="carousel-btn carousel-btn-prev"
                          onClick={(e) => {
                            e.stopPropagation();
                            prevImage(room.id, room.images.length);
                          }}
                          aria-label="Önceki resim"
                        >
                          ‹
                        </button>
                        <button 
                          className="carousel-btn carousel-btn-next"
                          onClick={() => nextImage(room.id, room.images.length)}
                          aria-label="Sonraki resim"
                        >
                          ›
                        </button>
                        <div className="carousel-indicators">
                          {room.images.map((_, index) => (
                            <span 
                              key={index} 
                              className={`indicator ${index === currentIndex ? 'active' : ''}`}
                              onClick={() => setCurrentImageIndex(prev => ({ ...prev, [room.id]: index }))}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              
              <div className="room-type-content">
                <h2>{room.room_number}</h2>
                
                {room.view_type && (
                  <div className="room-meta">
                    <span className="room-view">🌅 {room.view_type}</span>
                    {room.floor && <span className="room-floor">🏢 {room.floor}. Kat</span>}
                  </div>
                )}
                
                <p className="room-description">{room.description || 'Konforlu ve ferah odamız.'}</p>
                
                <div className="room-features">
                  <h3>Oda Özellikleri</h3>
                  <ul>
                    {room.custom_features && room.custom_features.length > 0 ? (
                      room.custom_features.map((feature, index) => (
                        <li key={index}>
                          <span className="feature-icon">✓</span>
                          {feature}
                        </li>
                      ))
                    ) : room.amenities && room.amenities.length > 0 ? (
                      room.amenities.map((amenity, index) => (
                        <li key={index}>
                          <span className="feature-icon">✓</span>
                          {amenity}
                        </li>
                      ))
                    ) : (
                      <li><span className="feature-icon">✓</span>Standart Oda Özellikleri</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
            );
          })}
        </div>

        <div className="reservation-cta">
          <h2>Rezervasyon Yapmak İster misiniz?</h2>
          <p>Müsaitlik sorgulama ve rezervasyon için tarihlerinizi seçin</p>
          <button 
            onClick={() => {
              if (onNavigateToReservation) {
                onNavigateToReservation();
              } else {
                window.location.hash = 'reservation';
              }
            }} 
            className="btn-primary-large"
          >
            Rezervasyon Yap
          </button>
        </div>
      </div>

      {lightboxOpen && activeRoomId && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>×</button>
            {(() => {
              const activeRoom = rooms.find(r => r.id === activeRoomId);
              if (!activeRoom || !activeRoom.images || activeRoom.images.length === 0) return null;
              
              const currentIndex = currentImageIndex[activeRoomId] || 0;
              const image = activeRoom.images[currentIndex];
              
              return (
                <>
                  <div className="lightbox-image-container">
                    <img 
                      key={getImageUrl(image.url)}
                      src={getImageUrl(image.url)} 
                      alt={image.caption || activeRoom.room_type_name} 
                      className="lightbox-image"
                      style={{ animation: 'fadeIn 0.35s ease' }}
                    />
                  </div>
                  {activeRoom.images.length > 1 && (
                    <>
                      <button 
                        className="lightbox-nav lightbox-prev"
                        onClick={() => prevImage(activeRoomId, activeRoom.images.length)}
                      >‹</button>
                      <button 
                        className="lightbox-nav lightbox-next"
                        onClick={() => nextImage(activeRoomId, activeRoom.images.length)}
                      >›</button>
                      <div className="lightbox-caption">
                         {currentIndex + 1} / {activeRoom.images.length}
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default OurRooms;
